/*
 * <BeWindow> / <BeWindowTab> -- React wrappers for the .be-window and
 * .be-window-tab CSS components.
 *
 * Renders the BeOS R5 B_TITLED_WINDOW_LOOK chrome. The yellow tab shows close
 * and/or zoom buttons based on which handlers you pass:
 *   - onClose + onZoom -> both buttons (a normal window)
 *   - onClose only     -> close-only tab
 *   - neither          -> a button-less tab (dialogs / alerts)
 * (There is no "active window with disabled buttons" in R5 -- an active window's
 * close/zoom are always live; only INACTIVE windows use the grey palette.)
 *
 * Pass `style` (e.g. position:absolute + top/left) to place several windows on a
 * desktop and overlap them; `inactive` renders the unfocused grey palette for
 * the windows behind the front one.
 *
 * Stacking: pass `tabs` (an array of tab descriptors) instead of the single
 * title/onClose/onZoom to render a .be-window__tabs row -- the tab-like
 * stacked-window interface. The selected tab keeps the 1px seam strip into the
 * body; tabs with state "unselected" are grey with no strip.
 *
 * The matching CSS lives in components/window.css and is imported via the
 * library's index.scss; consumers load the CSS bundle separately.
 */

import {
	type CSSProperties,
	type KeyboardEvent,
	type MouseEventHandler,
	type ReactEventHandler,
	type ReactNode,
	type PointerEvent as ReactPointerEvent,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import { type BeProps, beComponent } from "./create.tsx";

/**
 * `window_look` (Window.h). What CHROME the server draws -- distinct from feel,
 * which is window-manager behaviour and has no CSS analogue.
 *
 * BeBook, verbatim:
 *   titled    "A normal window with a border and title bar."
 *   document  "Like a titled window, only with a resize dragger in the corner."
 *   modal     "A window with a border but no title bar."
 *   floating  "A titled window with a thin border and small title bar."
 *   bordered  "This is a minimal window, with a thin border and little else."
 *
 * "document" adds no chrome class of its own -- it is a titled window plus the
 * corner dragger, so pass `resizable="knob"` with it. "no-border" is not in the
 * BeBook's five; it comes from the header (B_NO_BORDER_WINDOW_LOOK = 19), which
 * GenerateDecor maps to a bare SWindowDecor that draws nothing.
 */
export type BeWindowLook = "titled" | "document" | "floating" | "modal" | "bordered" | "no-border";

const LOOK_CLASS: Partial<Record<BeWindowLook, string>> = {
	floating: "is-floating",
	modal: "is-modal",
	bordered: "is-bordered",
	"no-border": "is-borderless",
};

/** Visual state of a window tab. */
export type BeWindowTabState =
	/** The selected tab of the focused window: yellow, seam strip. */
	| "active"
	/** The selected tab of an unfocused window: grey, seam strip. */
	| "inactive"
	/** A stacked tab that is not the window's front tab: grey, no strip. */
	| "unselected";

export type BeWindowTabProps = BeProps<
	"div",
	{
		/** Title text. */
		title?: string;
		/** Tab state; "inactive" may also be inherited from the host window. */
		state?: BeWindowTabState;
		/** Grey (inactive) palette -- legacy alias for state="inactive". */
		inactive?: boolean;
		/** If omitted, no close button. */
		onClose?: MouseEventHandler<HTMLButtonElement>;
		/** If omitted, no zoom button. */
		onZoom?: MouseEventHandler<HTMLButtonElement>;
		/** Activate the tab itself, by click or Enter/Space (e.g. select it in a stack). */
		onSelect?: ReactEventHandler<HTMLDivElement>;
		/**
		 * The B_FLOATING_WINDOW_LOOK Title Tab: 15px instead of 19, 12x12 glyphs
		 * instead of 14x14, and the plain title font rather than the bold one.
		 */
		mini?: boolean;
	}
>;

/**
 * <BeWindowTab> -- the BeOS window tab as a standalone component
 * (.be-window-tab): no window body required. Used on its own for the Figma
 * "Window Decoration" variant set, and by <BeWindow tabs={...}> for stacks.
 */
export const BeWindowTab = beComponent<BeWindowTabProps, HTMLDivElement>("BeWindowTab", (props, be) => {
	const { title, state, inactive, onClose, onZoom, onSelect, mini, scale, className, style, ...rest } = props;
	const effective = state ?? (inactive ? "inactive" : "active");
	const root = be(
		"be-window-tab",
		effective === "inactive" && "is-inactive",
		effective === "unselected" && "is-unselected",
		mini && "is-floating",
	);

	// Selectable tabs (stacks) get button semantics on the tab surface itself;
	// the close/zoom children stay independently focusable real <button>s.
	const selectable = onSelect
		? {
				role: "button" as const,
				tabIndex: 0,
				onClick: onSelect,
				onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						onSelect(e);
					}
				},
			}
		: undefined;

	// Gadget clicks are gadget clicks, not tab clicks -- R5's MouseDown
	// dispatches close/zoom before the tab hit-test, so don't let them bubble
	// into onSelect.
	const gadget =
		(handler?: MouseEventHandler<HTMLButtonElement>): MouseEventHandler<HTMLButtonElement> =>
		(e) => {
			e.stopPropagation();
			handler?.(e);
		};

	return (
		<div {...rest} {...selectable} {...root}>
			{onClose && <button type="button" className="be-window__close" aria-label="Close" onClick={gadget(onClose)} />}
			{title !== undefined && <span className="be-window__title be-text--tab be-pixel-text">{title}</span>}
			{onZoom && <button type="button" className="be-window__zoom" aria-label="Zoom" onClick={gadget(onZoom)} />}
		</div>
	);
});

/** One tab in a <BeWindow tabs={...}> stack. */
export type BeWindowStackTab = Omit<BeWindowTabProps, "scale" | "className"> & {
	/** React key; defaults to the title. */
	key?: string;
};

export type BeWindowProps = BeProps<
	"div",
	{
		/** Title text rendered in the yellow tab. */
		title?: string;

		/** Render the unfocused (grey) palette -- a window behind the active one. */
		inactive?: boolean;

		/**
		 * window_look. Defaults to "titled". "modal" and "bordered" draw NO title
		 * tab at all (a border and nothing else), so title/onClose/onZoom have
		 * nothing to render into.
		 */
		look?: BeWindowLook;

		/** If omitted, no close button is rendered. */
		onClose?: MouseEventHandler<HTMLButtonElement>;

		/** If omitted, no zoom button is rendered. */
		onZoom?: MouseEventHandler<HTMLButtonElement>;

		/**
		 * Stacked-tab row (window stacking). When given, replaces the single tab;
		 * title/onClose/onZoom on the window itself are ignored. On an inactive
		 * window every tab greys out; "unselected" tabs additionally lose the seam
		 * strip into the body.
		 */
		tabs?: BeWindowStackTab[];

		/**
		 * Make the window drag-resizable and show its resize affordance. R5 has TWO
		 * (SBeDecor::Decorate step 6):
		 *   - "notch" (the default for `true`): a plain resizable TITLED window --
		 *     two #989898 ticks etched into the inner border 22px in from the
		 *     bottom-right corner. This is what most resizable app windows show.
		 *   - "knob": a DOCUMENT window -- the 14px box + 1+2+3+4 dot grid that sits
		 *     over the B_V x B_H scrollbar corner gap. Use with <BeScrollView corner>.
		 * See minWidth/minHeight for the size floor.
		 */
		resizable?: boolean | "notch" | "knob";

		/**
		 * Resize floor, in CSS px of the CONTENT box -- BWindow::SetSizeLimits.
		 *
		 * Left unset, the floor is measured rather than guessed: the width cannot go
		 * below the tab's own intrinsic width (title + close/zoom gadgets), which is
		 * what R5's decorator enforces, and the height cannot go below one content
		 * line. A fixed magic minimum lets a caller drag the window narrower than its
		 * own chrome, at which point text reflows into a column one word wide.
		 *
		 * Pass a number to set a real limit for content that needs one.
		 */
		minWidth?: number;
		minHeight?: number;

		/** Inline style merged onto .be-window__content (lets callers size the body). */
		contentStyle?: CSSProperties;

		/**
		 * Mount the content flush to the frame, for content that composes its own
		 * chrome -- a <BeScrollView>, table, or status strip -- like Tracker mounts
		 * its pose view. (Auto-propagation from a nested control is deferred to
		 * #381.)
		 *
		 * The inset follows the LOOK, because it exists to clear frame strokes:
		 * 5px under the titled look, whose frame is drawn inside the border box,
		 * and 0 under floating/bordered/borderless, which draw outward.
		 */
		flush?: boolean;
		/**
		 * Take the box this is mounted in, and let the CONTENT scroll rather than
		 * the window growing and its container scrolling -- which is what a window
		 * is. Required for a scroll view inside to have a bounded box to work in.
		 */
		fill?: boolean;

		children?: ReactNode;
	}
>;

export const BeWindow = beComponent<BeWindowProps, HTMLDivElement>("BeWindow", (props, be) => {
	const {
		title,
		inactive,
		look,
		onClose,
		onZoom,
		tabs,
		scale,
		resizable,
		fill,
		flush,
		style,
		contentStyle,
		className,
		children,
		...restAll
	} = props;
	const { minWidth, minHeight } = props;
	// minWidth/minHeight are read off `props`, so drop them from the DOM spread.
	const { minWidth: _mw, minHeight: _mh, ...rest } = restAll;

	const contentClass = ["be-window__content", flush ? "is-flush" : null].filter(Boolean).join(" ");

	const { ref: forwarded, ...root } = be("be-window", inactive && "is-inactive", fill && "is-fill", look ? LOOK_CLASS[look] : undefined);

	// Drag-to-resize: the knob grows/shrinks the content box from its size at
	// pointer-down (the ResizeClick corner loop). The root element is needed
	// internally for the tab measurement, so mirror it out to the caller.
	const contentRef = useRef<HTMLDivElement>(null);
	const rootRef = useRef<HTMLDivElement>(null);
	useImperativeHandle(forwarded, () => rootRef.current as never, []);
	const [size, setSize] = useState<{ width: number; height: number } | null>(null);

	const onResizeStart = (e: ReactPointerEvent<HTMLDivElement>) => {
		const content = contentRef.current;
		if (!content) return;
		e.preventDefault();
		const rect = content.getBoundingClientRect();
		const startX = e.clientX;
		const startY = e.clientY;

		// Floor, measured at pointer-down so it tracks the real chrome and font.
		// Width: the tab's intrinsic width -- a window narrower than its own title
		// bar is what R5's decorator refuses, and what makes body text reflow into
		// a one-word column. Height: one line box, so the content never collapses
		// to a sliver. An explicit prop wins over both.
		const tab = rootRef.current?.querySelector<HTMLElement>(".be-window__tab, .be-window__tabs");
		const chromeW = tab ? tab.scrollWidth : 0;
		const lineH = Number.parseFloat(getComputedStyle(content).lineHeight) || 16;
		const floorW = minWidth ?? Math.max(chromeW, 64);
		const floorH = minHeight ?? Math.max(lineH, 32);

		const onMove = (ev: PointerEvent) => {
			setSize({
				width: Math.max(floorW, rect.width + (ev.clientX - startX)),
				height: Math.max(floorH, rect.height + (ev.clientY - startY)),
			});
		};
		const onUp = () => {
			window.removeEventListener("pointermove", onMove);
			window.removeEventListener("pointerup", onUp);
		};
		window.addEventListener("pointermove", onMove);
		window.addEventListener("pointerup", onUp);
	};

	const mergedContentStyle: CSSProperties | undefined = size ? { ...contentStyle, width: size.width, height: size.height } : contentStyle;

	return (
		<div {...rest} {...root} ref={rootRef}>
			{tabs ? (
				<div className="be-window__tabs">
					{tabs.map(({ key, ...tab }) => (
						<BeWindowTab key={key ?? tab.title} {...tab} />
					))}
				</div>
			) : (
				<div className="be-window__tab">
					{onClose && <button type="button" className="be-window__close" aria-label="Close" onClick={onClose} />}
					{title !== undefined && <span className="be-window__title be-text--tab be-pixel-text">{title}</span>}
					{onZoom && <button type="button" className="be-window__zoom" aria-label="Zoom" onClick={onZoom} />}
				</div>
			)}
			<div className={contentClass} style={mergedContentStyle} ref={contentRef}>
				{children}
			</div>
			{resizable &&
				(resizable === "knob" ? (
					<div className="be-window__resize" title="Resize" onPointerDown={onResizeStart} />
				) : (
					<div className="be-window__resize-notch" title="Resize" onPointerDown={onResizeStart} />
				))}
		</div>
	);
});
